/**
 * MediaCleanupDialog — iter174f
 *
 * Modal für Admin-Cleanup verwaister Medien. 2-Schritte-Flow:
 *   1. Scan: POST /admin/media/cleanup/scan (older_than_days)
 *   2. Nach expliziter Bestätigung: POST /admin/media/cleanup/execute mit
 *      ausgewählten file_ids.
 * Sicherheitsnetz: Backend prüft usage nochmal beim Execute.
 */
import { useState, useEffect } from "react";
import { X, Loader2, AlertTriangle, Trash2, CheckCircle2, CalendarClock } from "lucide-react";
import api from "@/lib/api";

function fmtBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function MediaCleanupDialog({ onClose }) {
  const [days, setDays] = useState(7);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmed, setConfirmed] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [autoScan, setAutoScan] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/admin/media/cleanup/last-scan")
      .then((r) => { if (alive) setAutoScan(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const runScan = async () => {
    setScanning(true); setError(""); setResult(null); setConfirmed(false);
    try {
      const r = await api.post("/admin/media/cleanup/scan", null, { params: { older_than_days: days }, timeout: 115000 });
      setScanResult(r.data);
      setSelectedIds(new Set((r.data.candidates || []).map((c) => c.file_id)));
    } catch (e) {
      setError(e?.response?.data?.detail || "Scan fehlgeschlagen");
    } finally { setScanning(false); }
  };

  const toggle = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };

  const runExecute = async () => {
    if (!confirmed || selectedIds.size === 0) return;
    setExecuting(true); setError("");
    try {
      const r = await api.post("/admin/media/cleanup/execute", {
        file_ids: Array.from(selectedIds),
      });
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally { setExecuting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
         data-testid="media-cleanup-dialog">
      <div className="bg-white w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-lg">
        <div className="p-4 border-b border-[#E5E5E5] flex items-center justify-between">
          <h3 className="text-sm font-bold flex items-center gap-2">
            <Trash2 size={14} /> Verwaiste Medien bereinigen
          </h3>
          <button onClick={onClose} data-testid="media-cleanup-close" className="p-1 hover:bg-[#F5F5F5]">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {autoScan && autoScan.last_run_at && (
            <div data-testid="media-cleanup-autoscan"
                 className="flex items-start gap-2 rounded border border-[#E5E5E5] bg-[#FAFAFA] px-3 py-2 text-[11px] text-[#555]">
              <CalendarClock size={14} className="mt-0.5 shrink-0 text-[#888]" />
              <span>
                Automatischer Tages-Scan (älter als {autoScan.older_than_days || 7} Tage):
                <b> {autoScan.count}</b> verwaiste Dateien
                {typeof autoScan.total_size_bytes === "number" && <> (≈ <b>{fmtBytes(autoScan.total_size_bytes)}</b>)</>}.
                {autoScan.count > 0 && " Starte unten einen Scan, um sie zu prüfen und freizugeben."}
                <span className="block text-[#999] mt-0.5">
                  Zuletzt geprüft: {new Date(autoScan.last_run_at).toLocaleString("de-DE")}
                  {autoScan.partial && " · Zeitlimit erreicht, evtl. mehr vorhanden"}
                </span>
              </span>
            </div>
          )}
          <div className="flex items-end gap-2">
            <label className="text-xs">
              Älter als
              <input
                type="number" min="0" max="365" value={days}
                onChange={(e) => setDays(parseInt(e.target.value, 10) || 0)}
                data-testid="media-cleanup-days"
                className="ml-2 h-8 w-20 border border-[#DDD] px-2 text-sm"
              />
              <span className="ml-1 text-[11px] text-[#666]">Tage</span>
            </label>
            <button
              data-testid="media-cleanup-scan"
              onClick={runScan}
              disabled={scanning}
              className="h-8 px-3 text-xs font-semibold border border-[#0A0A0A] hover:bg-[#F5F5F5] flex items-center gap-1.5"
            >
              {scanning ? <Loader2 size={12} className="animate-spin" /> : null}
              Scan starten
            </button>
          </div>

          {error && (
            <div className="p-2 bg-red-50 border border-red-200 text-xs text-red-700 flex items-center gap-1.5">
              <AlertTriangle size={12} /> {String(error)}
            </div>
          )}

          {scanResult && !result && (
            <>
              <p className="text-xs text-[#666]" data-testid="media-cleanup-summary">
                <b>{scanResult.count}</b> verwaiste Dateien gefunden (≈ <b>{fmtBytes(scanResult.total_size_bytes)}</b>).
                {scanResult.count === 0 && " Keine Aktion nötig."}
              </p>
              {scanResult.partial && (
                <p data-testid="media-cleanup-partial" className="text-[11px] text-amber-700 bg-amber-50 border border-amber-300 rounded px-2 py-1 mb-2">
                  Zeitlimit erreicht ({scanResult.scanned} Dateien geprüft). Es könnten weitere verwaiste Dateien existieren – lösche zunächst diese und starte den Scan danach erneut.
                </p>
              )}
              {scanResult.count > 0 && (
                <>
                  <div className="border border-[#E5E5E5] max-h-64 overflow-y-auto">
                    {scanResult.candidates.map((c) => (
                      <label key={c.file_id}
                             data-testid={`media-cleanup-row-${c.file_id}`}
                             className="flex items-center gap-2 px-2 py-1.5 border-b border-[#F0F0F0] text-[11px] cursor-pointer hover:bg-[#FAFAFA]">
                        <input type="checkbox"
                               checked={selectedIds.has(c.file_id)}
                               onChange={() => toggle(c.file_id)} />
                        <span className="flex-1 truncate font-mono">{c.storage_path || c.file_id}</span>
                        <span className="text-[#999]">{fmtBytes(c.size_bytes)}</span>
                      </label>
                    ))}
                  </div>
                  <label className="flex items-start gap-2 text-xs">
                    <input type="checkbox"
                           data-testid="media-cleanup-confirm"
                           checked={confirmed}
                           onChange={(e) => setConfirmed(e.target.checked)} />
                    <span>Ich bestätige, dass die ausgewählten <b>{selectedIds.size}</b> Dateien
                      dauerhaft gelöscht werden sollen. Backend prüft usage nochmals als Sicherheitsnetz.</span>
                  </label>
                  <button
                    data-testid="media-cleanup-execute"
                    onClick={runExecute}
                    disabled={!confirmed || selectedIds.size === 0 || executing}
                    className="h-9 px-4 text-xs font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 flex items-center gap-2"
                  >
                    {executing ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    {selectedIds.size} Dateien löschen
                  </button>
                </>
              )}
            </>
          )}

          {result && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 text-xs" data-testid="media-cleanup-result">
              <p className="flex items-center gap-1.5 font-semibold text-emerald-800">
                <CheckCircle2 size={14} /> Aufräumen abgeschlossen
              </p>
              <p className="mt-1 text-emerald-700">
                Gelöscht: <b>{result.deleted_count}</b> · Übersprungen: <b>{result.skipped_count}</b>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
